import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next"; 
import {
  Button,
  Modal,
  Box,
  Switch,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  BranchService,
  EmployeeAvailabilityForTips,
  ManualTipsReprocessStatus,
  PendingManualTip,
  ProcessTipsHistoryItem,
} from "../../services/branch-service";
import { useToast } from "../../context/toast-context";
import { useUser } from "../../context/user-context";
import { CashCoinIcon } from "../icons/CashCoin";
import { LoadingModal } from "../LoadingModal";

const STATUS_POLL_INTERVAL_MS = 5000;
const STATUS_POLL_TIMEOUT_MS = 120000;

const ERROR_IN_PROCESS = 153;
const ERROR_COOLDOWN = 154;
const ERROR_NO_PENDING = 155;

function formatTimeFromHistory(createdAt: string): string {
  try {
    const d = new Date(createdAt);
    const hours = d.getHours().toString().padStart(2, "0");
    const minutes = d.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  } catch {
    return "--:--";
  }
}

function formatTipDate(value: string | Date | null | undefined): string {
  if (!value) return "-";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, "0");
    const minutes = d.getMinutes().toString().padStart(2, "0");
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return "-";
  }
}

function formatTipAmount(amount: string | number): string {
  const n = typeof amount === "number" ? amount : Number(amount);
  if (Number.isNaN(n)) return String(amount);
  return n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getHistoryItemLabel(
  item: ProcessTipsHistoryItem,
  t: (key: string, options?: Record<string, unknown>) => string
): string | null {
  const time = formatTimeFromHistory(item.createdAt);
  const failed = item.failedTipsCount ?? 0;
  const total = item.totalTipsCount ?? 0;

  if (failed > 0) {
    return t("branches.process-tips.history-item-errors", {
      time,
      count: failed,
    });
  }

  // Mientras el lote no terminó, no listamos el ítem (el hint del botón ya indica que hay proceso).
  if (total > 0 && !item.completed) {
    return null;
  }

  return t("branches.process-tips.history-item", { time });
}

function extractCooldownSeconds(errorData?: unknown): number | undefined {
  if (!errorData || typeof errorData !== "object") return undefined;
  const data = errorData as Record<string, unknown>;

  if (typeof data.secondsRemaining === "number") return data.secondsRemaining;
  if (typeof data.SecondsRemaining === "number") return data.SecondsRemaining;

  const params = data.Params ?? data.params;
  if (params && typeof params === "object") {
    const p = params as Record<string, unknown>;
    if (typeof p.seconds === "number") return p.seconds;
    if (typeof p.Seconds === "number") return p.Seconds;
    if (typeof p.secondsRemaining === "number") return p.secondsRemaining;
  }

  return undefined;
}

function formatCalendarDate(value: string): string {
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

function getStatusHint(
  status: ManualTipsReprocessStatus | null,
  t: (key: string, options?: Record<string, unknown>) => string
): string | null {
  if (!status) return null;

  if (status.reason === "COOLDOWN") {
    return null;
  }

  if (status.reason === "IN_PROCESS") {
    return t("branches.process-tips.in-process");
  }

  if (!status.canReprocess && status.pendingTipsCount === 0) {
    return t("branches.process-tips.no-pending");
  }

  if (!status.canReprocess) {
    return t("branches.process-tips.no-pending");
  }

  return null;
}

interface ModalManualTipsToggleProps {
  branchId: number;
  manualTipsProccess?: boolean;
  onToggle?: (branchId: number, newValue: boolean) => void;
}

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxWidth: 1200,
  height: "90vh",
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "#fff",
  border: "1px solid #898989",
  boxShadow: 24,
  borderRadius: 4,
};

const confirmationModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "400px",
  bgcolor: "#fff",
  border: "1px solid #898989",
  boxShadow: 24,
  borderRadius: 4,
};

export function ModalManualTipsToggle({
  branchId,
  manualTipsProccess = false,
  onToggle,
}: ModalManualTipsToggleProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingTips, setIsProcessingTips] = useState(false);
  const [isReprocessRunning, setIsReprocessRunning] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [pendingValue, setPendingValue] = useState<boolean | null>(null);
  const [showProcessButton, setShowProcessButton] = useState(false);
  const [processTipsHistory, setProcessTipsHistory] = useState<ProcessTipsHistoryItem[]>([]);
  const [pendingTips, setPendingTips] = useState<PendingManualTip[]>([]);
  const [selectedTipIds, setSelectedTipIds] = useState<number[]>([]);
  const [isLoadingPendingTips, setIsLoadingPendingTips] = useState(false);
  const [processStep, setProcessStep] = useState<"tips" | "availability">("tips");
  const [availability, setAvailability] = useState<EmployeeAvailabilityForTips | null>(null);
  const [selectedAvailabilityDate, setSelectedAvailabilityDate] = useState<string>("");
  const [availabilityBulkScope, setAvailabilityBulkScope] = useState<"day" | "all">("day");
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [isSavingAvailability, setIsSavingAvailability] = useState(false);
  const [availabilitySaved, setAvailabilitySaved] = useState(false);
  const [reprocessStatus, setReprocessStatus] = useState<ManualTipsReprocessStatus | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const postClickStartedAtRef = useRef<number | null>(null);
  const isPostClickPollingRef = useRef(false);
  const reprocessStatusRef = useRef(reprocessStatus);
  reprocessStatusRef.current = reprocessStatus;

  const { t } = useTranslation();
  const { showToast } = useToast();
  const { user } = useUser();
  const branchService = useMemo(() => new BranchService(), []);

  const stopStatusPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const loadReprocessStatus = useCallback(() => {
    setIsLoadingStatus(true);
    branchService.getManualTipsReprocessStatus(
      branchId,
      (data) => {
        setReprocessStatus(data);
        setIsLoadingStatus(false);
      },
      () => {
        setReprocessStatus(null);
        setIsLoadingStatus(false);
      },
      user?.userId
    );
  }, [branchId, branchService, user?.userId]);

  const loadProcessTipsHistory = useCallback(() => {
    branchService.getProcessTipsHistory(
      branchId,
      (data) => setProcessTipsHistory(Array.isArray(data) ? data : []),
      () => setProcessTipsHistory([]),
      user?.userId
    );
  }, [branchId, branchService, user?.userId]);

  const loadPendingTips = useCallback(() => {
    setIsLoadingPendingTips(true);
    branchService.getPendingTipsManualProcess(
      branchId,
      (data) => {
        const tips = Array.isArray(data) ? data : [];
        setPendingTips(tips);
        setSelectedTipIds((current) => {
          const availableIds = new Set(tips.map((tip) => tip.tipId));
          const stillSelected = current.filter((id) => availableIds.has(id));
          if (stillSelected.length > 0) {
            return stillSelected;
          }
          return tips.map((tip) => tip.tipId);
        });
        setIsLoadingPendingTips(false);
      },
      () => {
        setPendingTips([]);
        setSelectedTipIds([]);
        setIsLoadingPendingTips(false);
      },
      user?.userId
    );
  }, [branchId, branchService, user?.userId]);

  const startStatusPolling = useCallback(
    (afterSuccessfulClick: boolean) => {
      stopStatusPolling();

      if (afterSuccessfulClick) {
        isPostClickPollingRef.current = true;
        postClickStartedAtRef.current = Date.now();
      }

      const tick = () => {
        if (
          isPostClickPollingRef.current &&
          postClickStartedAtRef.current &&
          Date.now() - postClickStartedAtRef.current >= STATUS_POLL_TIMEOUT_MS
        ) {
          isPostClickPollingRef.current = false;
          postClickStartedAtRef.current = null;
          setIsReprocessRunning(false);
          stopStatusPolling();
          setProcessStep("tips");
          loadProcessTipsHistory();
          loadPendingTips();
          return;
        }

        branchService.getManualTipsReprocessStatus(
          branchId,
          (data) => {
            setReprocessStatus(data);
            setIsLoadingStatus(false);

            // El lote terminó cuando ya no hay tips IN_PROCESS.
            // No esperamos canReprocess: tras terminar suele haber cooldown y el overlay
            // quedaba colgado ~90s aunque el historial ya mostrara el resultado.
            if (isPostClickPollingRef.current && data.inProcessCount === 0) {
              isPostClickPollingRef.current = false;
              postClickStartedAtRef.current = null;
              setIsReprocessRunning(false);
              setProcessStep("tips");
              loadProcessTipsHistory();
              loadPendingTips();

              if (data.reason !== "COOLDOWN") {
                stopStatusPolling();
              }
              return;
            }

            if (!isPostClickPollingRef.current && data.canReprocess) {
              stopStatusPolling();
              loadProcessTipsHistory();
              loadPendingTips();
            }

            if (!isPostClickPollingRef.current && data.reason !== "COOLDOWN" && data.inProcessCount === 0) {
              stopStatusPolling();
            }
          },
          () => setIsLoadingStatus(false),
          user?.userId
        );
      };

      tick();
      pollIntervalRef.current = setInterval(tick, STATUS_POLL_INTERVAL_MS);
    },
    [branchId, branchService, loadPendingTips, loadProcessTipsHistory, stopStatusPolling, user?.userId]
  );

  const historyItems = useMemo(
    () =>
      [...processTipsHistory]
        .filter((item) => item.createdAt)
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
        .map((item) => ({ item, label: getHistoryItemLabel(item, t) }))
        .filter((entry): entry is { item: ProcessTipsHistoryItem; label: string } =>
          entry.label != null
        ),
    [processTipsHistory, t]
  );

  const statusHint = useMemo(
    () => getStatusHint(reprocessStatus, t),
    [reprocessStatus, t]
  );

  const canReprocess = reprocessStatus?.canReprocess ?? false;
  const allPendingSelected =
    pendingTips.length > 0 && selectedTipIds.length === pendingTips.length;
  const isReprocessDisabled =
    isProcessingTips ||
    isReprocessRunning ||
    isLoadingStatus ||
    isLoadingPendingTips ||
    !canReprocess ||
    selectedTipIds.length === 0 ||
    (processStep === "availability" && !availabilitySaved);

  useEffect(() => {
    if (!isModalOpen || !showProcessButton) {
      stopStatusPolling();
      setReprocessStatus(null);
      isPostClickPollingRef.current = false;
      postClickStartedAtRef.current = null;
      setProcessTipsHistory([]);
      setPendingTips([]);
      setSelectedTipIds([]);
      setProcessStep("tips");
      setAvailability(null);
      setSelectedAvailabilityDate("");
      setAvailabilityBulkScope("day");
      setAvailabilitySaved(false);
      return;
    }

    loadProcessTipsHistory();
    loadPendingTips();

    branchService.getManualTipsReprocessStatus(
      branchId,
      (data) => {
        setReprocessStatus(data);
        if (!data.canReprocess) {
          startStatusPolling(false);
        }
      },
      () => setReprocessStatus(null),
      user?.userId
    );

    return stopStatusPolling;
  }, [
    isModalOpen,
    showProcessButton,
    branchId,
    branchService,
    loadPendingTips,
    loadProcessTipsHistory,
    startStatusPolling,
    stopStatusPolling,
    user?.userId,
  ]);

  useEffect(() => () => stopStatusPolling(), [stopStatusPolling]);

  const handleToggle = async () => {
    if (pendingValue === null) return;

    setIsLoading(true);

    try {
      await branchService.updateManualProcessTips(
        branchId,
        pendingValue,
        () => {
          showToast(
            pendingValue
              ? t("branches.manual-process.enabled")
              : t("branches.manual-process.automatic-disabled"),
            "success"
          );
          onToggle?.(branchId, pendingValue);
          setIsConfirmationModalOpen(false);

          if (pendingValue) {
            setShowProcessButton(true);
            loadReprocessStatus();
          } else {
            setIsModalOpen(false);
          }

          setPendingValue(null);
        },
        (errorMessage) => {
          showToast(errorMessage, "error");
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
    setShowProcessButton(manualTipsProccess);
  };

  const handleModalClose = () => {
    if (isProcessingTips || isReprocessRunning) return;

    setIsModalOpen(false);
    setPendingValue(null);
    setShowProcessButton(false);
    setIsReprocessRunning(false);
    setProcessStep("tips");
    setAvailability(null);
    setSelectedAvailabilityDate("");
    setAvailabilityBulkScope("day");
    setAvailabilitySaved(false);
    stopStatusPolling();
    isPostClickPollingRef.current = false;
    postClickStartedAtRef.current = null;
  };

  const isPageLoading =
    isProcessingTips || isReprocessRunning || isLoadingAvailability || isSavingAvailability;

  const handleSwitchChange = (newValue: boolean) => {
    if (newValue !== manualTipsProccess) {
      setPendingValue(newValue);
      setIsConfirmationModalOpen(true);
    }
  };

  const handleConfirmationCancel = () => {
    setIsConfirmationModalOpen(false);
    setPendingValue(null);
  };

  const handleRetryError = useCallback(
    (errorMessage: string, errorCode?: number, errorData?: unknown) => {
      setIsReprocessRunning(false);
      const cooldownSeconds = extractCooldownSeconds(errorData);

      if (errorCode === ERROR_IN_PROCESS) {
        showToast(t("branches.process-tips.error-in-process"), "error");
      } else if (errorCode === ERROR_COOLDOWN) {
        showToast(
          t("branches.process-tips.error-cooldown", {
            seconds: cooldownSeconds ?? reprocessStatusRef.current?.secondsRemaining ?? "?",
          }),
          "error"
        );
      } else if (errorCode === ERROR_NO_PENDING) {
        showToast(t("branches.process-tips.error-no-pending"), "error");
      } else {
        showToast(errorMessage, "error");
      }

      loadReprocessStatus();
      loadPendingTips();
      if (!reprocessStatusRef.current?.canReprocess) {
        startStatusPolling(false);
      }
    },
    [loadPendingTips, loadReprocessStatus, showToast, startStatusPolling, t]
  );

  const handleToggleTipSelection = (tipId: number) => {
    setAvailabilitySaved(false);
    setSelectedTipIds((current) =>
      current.includes(tipId)
        ? current.filter((id) => id !== tipId)
        : [...current, tipId]
    );
  };

  const handleToggleSelectAllTips = () => {
    setAvailabilitySaved(false);
    if (allPendingSelected) {
      setSelectedTipIds([]);
      return;
    }
    setSelectedTipIds(pendingTips.map((tip) => tip.tipId));
  };

  const handleOpenAvailability = async () => {
    if (selectedTipIds.length === 0) return;
    setIsLoadingAvailability(true);
    try {
      await branchService.getEmployeeAvailabilityForTips(
        branchId,
        selectedTipIds,
        (data) => {
          setAvailability(data);
          setSelectedAvailabilityDate(data.dates[0] ?? "");
          setAvailabilityBulkScope("day");
          setAvailabilitySaved(true);
          setProcessStep("availability");
        },
        (error) => showToast(error, "error"),
        user?.userId
      );
    } finally {
      setIsLoadingAvailability(false);
    }
  };

  const handleToggleEmployeeOnLeave = (userId: number, date: string, onLeave: boolean) => {
    setAvailabilitySaved(false);
    setAvailability((current) => {
      if (!current) return current;
      return {
        ...current,
        employees: current.employees.map((employee) =>
          employee.userId === userId
            ? {
                ...employee,
                availabilityByDate: {
                  ...employee.availabilityByDate,
                  [date]: onLeave,
                },
              }
            : employee
        ),
      };
    });
  };

  const handleSetAllEmployeesOnLeaveForDates = (dates: string[], onLeave: boolean) => {
    if (!availability || dates.length === 0) return;
    setAvailabilitySaved(false);
    setAvailability((current) => {
      if (!current) return current;
      return {
        ...current,
        employees: current.employees.map((employee) => {
          const availabilityByDate = { ...employee.availabilityByDate };
          for (const date of dates) {
            availabilityByDate[date] = onLeave;
          }
          return { ...employee, availabilityByDate };
        }),
      };
    });
  };

  const handleApplyDayConfigToDates = (sourceDate: string, targetDates: string[]) => {
    if (!availability || !sourceDate || targetDates.length === 0) return;
    const datesToApply = targetDates.filter((date) => date !== sourceDate);
    if (datesToApply.length === 0) return;

    setAvailabilitySaved(false);
    setAvailability((current) => {
      if (!current) return current;
      return {
        ...current,
        employees: current.employees.map((employee) => {
          const sourceOnLeave = employee.availabilityByDate[sourceDate] ?? false;
          const availabilityByDate = { ...employee.availabilityByDate };
          for (const date of datesToApply) {
            availabilityByDate[date] = sourceOnLeave;
          }
          return { ...employee, availabilityByDate };
        }),
      };
    });
  };

  const getAvailabilityBulkDates = () => {
    if (!availability) return [];
    if (availabilityBulkScope === "all") {
      return availability.dates;
    }
    return selectedAvailabilityDate ? [selectedAvailabilityDate] : [];
  };

  const handleBulkSetOnLeave = (onLeave: boolean) => {
    handleSetAllEmployeesOnLeaveForDates(getAvailabilityBulkDates(), onLeave);
  };

  const handleApplyCurrentDayToAll = () => {
    if (!availability || !selectedAvailabilityDate) return;
    handleApplyDayConfigToDates(selectedAvailabilityDate, availability.dates);
  };

  const handleSaveAvailability = async () => {
    if (!availability) return;
    const items = availability.employees.flatMap((employee) =>
      availability.dates.map((date) => ({
        UserId: employee.userId,
        AvailabilityDate: date,
        OnLeave: employee.availabilityByDate[date] ?? false,
      }))
    );
    if (items.length === 0) {
      showToast(t("branches.process-tips.availability-empty"), "error");
      return;
    }

    setIsSavingAvailability(true);
    try {
      await branchService.saveEmployeeAvailabilityForDates(
        branchId,
        items,
        () => {
          setAvailabilitySaved(true);
          showToast(t("branches.process-tips.availability-saved"), "success");
        },
        (error) => showToast(error, "error"),
        user?.userId
      );
    } finally {
      setIsSavingAvailability(false);
    }
  };

  const handleProcessTips = async () => {
    if (
      isProcessingTips ||
      isReprocessRunning ||
      !reprocessStatusRef.current?.canReprocess ||
      selectedTipIds.length === 0
    ) {
      return;
    }

    setIsProcessingTips(true);
    setIsReprocessRunning(true);

    try {
      await branchService.retryPendingTips(
        branchId,
        () => {
          showToast(t("branches.process-tips.success"), "success");
          setProcessStep("tips");
          loadProcessTipsHistory();
          loadReprocessStatus();
          loadPendingTips();
          startStatusPolling(true);
        },
        handleRetryError,
        user?.userId,
        allPendingSelected ? undefined : selectedTipIds
      );
    } finally {
      setIsProcessingTips(false);
    }
  };

  return (
    <>
      <Button
        title={t("branches.manual-process.label")}
        onClick={handleModalOpen}
        sx={{
          margin: 0,
          minWidth: "fit-content",
          background: "transparent",
          border: "1px solid #EF0BB8",
          borderRadius: "50%",
          color: "#EF0BB8",
          textTransform: "none",
          fontFamily: "Montserrat",
          fontWeight: 300,
          width: "2.5rem",
          height: "2.5rem",
          padding: "6px",
        }}
      >
        <CashCoinIcon />
      </Button>

      <Modal
        open={isModalOpen}
        onClose={handleModalClose}
        disableEscapeKeyDown={isPageLoading}
        aria-labelledby="manual-tips-modal"
        aria-describedby="manual-tips-modal-description"
      >
        <Box sx={modalStyle}>
          <div className="bg-gray-light text-center text-xl font-normal text-black py-8 px-2">
            <h1>{t("branches.manual-process.modal-title")}</h1>
          </div>
          <div className="p-8">
            <div className="text-center mb-6">
              <p className="text-sm text-gray-600 mb-6">
                {t("branches.manual-process.modal-description")}
              </p>
            </div>

            <div className="flex flex-col gap-4 mb-6">
              <div className="flex justify-center">
                <FormControlLabel
                  sx={{
                    "& .MuiTypography-root": {
                      fontFamily: "Montserrat",
                      color: "#000",
                      fontSize: "16px",
                      fontWeight: "500",
                    },
                  }}
                  control={
                    <Switch
                      checked={manualTipsProccess}
                      onChange={(e) => handleSwitchChange(e.target.checked)}
                      disabled={isLoading}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
                          bgcolor: "#EF0BB8",
                        },
                        "& .MuiSwitch-track": {
                          bgcolor: "#898989",
                        },
                        "& .MuiSwitch-thumb": {
                          bgcolor: "#EF0BB8",
                        },
                      }}
                    />
                  }
                  label={
                    manualTipsProccess
                      ? t("branches.manual-process.enable")
                      : t("branches.manual-process.disable")
                  }
                />
              </div>
            </div>

            {showProcessButton && (
              <div className="flex flex-col items-center mt-8">
                {processStep === "tips" ? (
                  <>
                    <div className="w-full overflow-x-auto mb-6">
                      <p className="text-sm text-gray-600 mb-3 text-center">
                        {t("branches.process-tips.pending-list-title", {
                          count: pendingTips.length,
                          selected: selectedTipIds.length,
                        })}
                      </p>
                      {isLoadingPendingTips ? (
                        <p className="text-sm text-gray-500 text-center">{t("loading")}</p>
                      ) : pendingTips.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center">
                          {t("branches.process-tips.empty-list")}
                        </p>
                      ) : (
                        <table className="w-full text-sm text-left border-collapse">
                          <thead>
                            <tr className="border-b border-gray-300">
                              <th className="py-2 px-2 w-10">
                                <Checkbox
                                  checked={allPendingSelected}
                                  indeterminate={
                                    selectedTipIds.length > 0 && !allPendingSelected
                                  }
                                  onChange={handleToggleSelectAllTips}
                                  disabled={isPageLoading}
                                  sx={{
                                    color: "#EF0BB8",
                                    "&.Mui-checked": { color: "#EF0BB8" },
                                    "&.MuiCheckbox-indeterminate": { color: "#EF0BB8" },
                                  }}
                                />
                              </th>
                              <th className="py-2 px-2 font-medium">{t("branches.process-tips.column-id")}</th>
                              <th className="py-2 px-2 font-medium">{t("branches.process-tips.column-date")}</th>
                              <th className="py-2 px-2 font-medium">{t("branches.process-tips.column-amount")}</th>
                              <th className="py-2 px-2 font-medium">{t("branches.process-tips.column-origin")}</th>
                              <th className="py-2 px-2 font-medium">{t("branches.process-tips.column-employee")}</th>
                              <th className="py-2 px-2 font-medium">{t("branches.process-tips.column-status")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pendingTips.map((tip) => (
                              <tr key={tip.tipId} className="border-b border-gray-200">
                                <td className="py-1 px-2">
                                  <Checkbox
                                    checked={selectedTipIds.includes(tip.tipId)}
                                    onChange={() => handleToggleTipSelection(tip.tipId)}
                                    disabled={isPageLoading}
                                    sx={{
                                      color: "#EF0BB8",
                                      "&.Mui-checked": { color: "#EF0BB8" },
                                    }}
                                  />
                                </td>
                                <td className="py-1 px-2">{tip.tipId}</td>
                                <td className="py-1 px-2">
                                  {formatTipDate(tip.externalTipCreatedDate ?? tip.createdDate)}
                                </td>
                                <td className="py-1 px-2">{formatTipAmount(tip.amount)}</td>
                                <td className="py-1 px-2">{tip.originName || "-"}</td>
                                <td className="py-1 px-2">{tip.employeeName || "-"}</td>
                                <td className="py-1 px-2">{tip.statusDescription}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>

                    <Button
                      onClick={handleOpenAvailability}
                      disabled={
                        isPageLoading ||
                        isLoadingPendingTips ||
                        selectedTipIds.length === 0
                      }
                      sx={{
                        backgroundColor: "#EF0BB8",
                        color: "#fff",
                        fontFamily: "Montserrat",
                        textTransform: "none",
                        fontWeight: "400",
                        padding: "12px 24px",
                        "&:hover": {
                          backgroundColor: "#d009a0",
                        },
                        "&:disabled": {
                          backgroundColor: "#ccc",
                          color: "#666",
                        },
                      }}
                    >
                      {isLoadingAvailability
                        ? t("loading")
                        : t("branches.process-tips.assign-schedules")}
                    </Button>
                  </>
                ) : (
                  <div className="w-full">
                    <p className="text-lg font-medium text-center mb-2">
                      {t("branches.process-tips.availability-title")}
                    </p>
                    <p className="text-sm text-gray-600 mb-4 text-center">
                      {t("branches.process-tips.availability-description")}
                    </p>

                    {availability && availability.dates.length > 0 && (
                      <>
                        <Tabs
                          value={selectedAvailabilityDate}
                          onChange={(_event, value) => setSelectedAvailabilityDate(value)}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{
                            mb: 2,
                            "& .MuiTab-root": { fontFamily: "Montserrat", textTransform: "none" },
                            "& .Mui-selected": { color: "#EF0BB8" },
                            "& .MuiTabs-indicator": { backgroundColor: "#EF0BB8" },
                          }}
                        >
                          {availability.dates.map((date) => (
                            <Tab
                              key={date}
                              value={date}
                              label={formatCalendarDate(date)}
                            />
                          ))}
                        </Tabs>

                        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={availabilityBulkScope}
                            onChange={(_event, value) => {
                              if (value) setAvailabilityBulkScope(value);
                            }}
                            disabled={isPageLoading}
                            sx={{
                              alignSelf: "center",
                              "& .MuiToggleButton-root": {
                                fontFamily: "Montserrat",
                                textTransform: "none",
                                fontSize: "12px",
                                px: 1.5,
                                py: 0.5,
                              },
                              "& .Mui-selected": {
                                color: "#EF0BB8 !important",
                                backgroundColor: "rgba(239, 11, 184, 0.08) !important",
                                borderColor: "#EF0BB8 !important",
                              },
                            }}
                          >
                            <ToggleButton value="day">
                              {t("branches.process-tips.bulk-scope-day")}
                            </ToggleButton>
                            <ToggleButton value="all">
                              {t("branches.process-tips.bulk-scope-all")}
                            </ToggleButton>
                          </ToggleButtonGroup>

                          <div className="flex items-center justify-center gap-3">
                            <button
                              type="button"
                              disabled={isPageLoading || getAvailabilityBulkDates().length === 0}
                              onClick={() => handleBulkSetOnLeave(false)}
                              className="text-sm font-medium text-[#10B981] disabled:text-gray-400"
                            >
                              {t("branches.process-tips.all-present")}
                            </button>
                            <span className="text-gray-300">|</span>
                            <button
                              type="button"
                              disabled={isPageLoading || getAvailabilityBulkDates().length === 0}
                              onClick={() => handleBulkSetOnLeave(true)}
                              className="text-sm font-medium text-[#EF4444] disabled:text-gray-400"
                            >
                              {t("branches.process-tips.all-absent")}
                            </button>
                            {availability.dates.length > 1 && (
                              <>
                                <span className="text-gray-300">|</span>
                                <button
                                  type="button"
                                  disabled={isPageLoading || !selectedAvailabilityDate}
                                  onClick={handleApplyCurrentDayToAll}
                                  className="text-sm font-medium text-[#EF0BB8] disabled:text-gray-400"
                                >
                                  {t("branches.process-tips.apply-day-to-all")}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {!availability || availability.employees.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center">
                        {t("branches.process-tips.availability-empty")}
                      </p>
                    ) : (
                      <table className="w-full text-sm text-left border-collapse mb-6">
                        <thead>
                          <tr className="border-b border-gray-300">
                            <th className="py-2 px-2 font-medium">
                              {t("branches.process-tips.column-employee")}
                            </th>
                            <th className="py-2 px-2 font-medium text-center">
                              {t("branches.process-tips.present")} / {t("branches.process-tips.absent")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {availability.employees.map((employee) => {
                            const onLeave =
                              employee.availabilityByDate[selectedAvailabilityDate] ?? false;
                            return (
                              <tr key={employee.userId} className="border-b border-gray-200">
                                <td className="py-2 px-2">{employee.name}</td>
                                <td className="py-2 px-2">
                                  <div className="flex justify-center">
                                    <FormControlLabel
                                      sx={{
                                        margin: 0,
                                        "& .MuiTypography-root": {
                                          fontFamily: "Montserrat",
                                          fontSize: "14px",
                                        },
                                      }}
                                      control={
                                        <Switch
                                          checked={!onLeave}
                                          onChange={(e) =>
                                            handleToggleEmployeeOnLeave(
                                              employee.userId,
                                              selectedAvailabilityDate,
                                              !e.target.checked
                                            )
                                          }
                                          disabled={isPageLoading}
                                          sx={{
                                            "& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track":
                                              { bgcolor: "#10B981" },
                                            "& .MuiSwitch-track": { bgcolor: "#EF4444" },
                                            "& .MuiSwitch-thumb": {
                                              bgcolor: onLeave ? "#EF4444" : "#10B981",
                                            },
                                          }}
                                        />
                                      }
                                      label={
                                        onLeave
                                          ? t("branches.process-tips.absent")
                                          : t("branches.process-tips.present")
                                      }
                                    />
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}

                    <div className="flex flex-wrap gap-3 justify-center">
                      <Button
                        onClick={() => setProcessStep("tips")}
                        disabled={isPageLoading}
                        sx={{
                          color: "#898989",
                          fontFamily: "Montserrat",
                          textTransform: "none",
                          fontWeight: "400",
                        }}
                      >
                        {t("branches.process-tips.availability-back")}
                      </Button>
                      <Button
                        onClick={handleSaveAvailability}
                        disabled={
                          isPageLoading ||
                          availabilitySaved ||
                          !availability ||
                          availability.employees.length === 0
                        }
                        sx={{
                          backgroundColor: availabilitySaved ? "#ccc" : "#EF0BB8",
                          color: "#fff",
                          fontFamily: "Montserrat",
                          textTransform: "none",
                          fontWeight: "400",
                          padding: "12px 24px",
                          "&:hover": {
                            backgroundColor: availabilitySaved ? "#ccc" : "#d009a0",
                          },
                          "&:disabled": { backgroundColor: "#ccc", color: "#666" },
                        }}
                      >
                        {isSavingAvailability
                          ? t("loading")
                          : t("branches.process-tips.availability-save")}
                      </Button>
                      <Button
                        onClick={handleProcessTips}
                        disabled={isReprocessDisabled}
                        sx={{
                          backgroundColor: availabilitySaved ? "#10B981" : "#ccc",
                          color: "#fff",
                          fontFamily: "Montserrat",
                          textTransform: "none",
                          fontWeight: "400",
                          padding: "12px 24px",
                          "&:hover": {
                            backgroundColor: availabilitySaved ? "#059669" : "#ccc",
                          },
                          "&:disabled": {
                            backgroundColor: "#ccc",
                            color: "#666",
                          },
                        }}
                      >
                        {isProcessingTips || isReprocessRunning || isLoadingStatus
                          ? t("loading")
                          : t("branches.process-tips.button")}
                      </Button>
                    </div>
                  </div>
                )}

                {statusHint && (
                  <p className="text-sm text-gray-600 mt-3 text-center max-w-5xl">
                    {statusHint}
                  </p>
                )}

                <p className="text-sm text-gray-600 mt-3 text-center max-w-5xl">
                  {t("branches.process-tips.schedule-note")}
                </p>

                {historyItems.length > 0 && (
                  <div className="w-full max-w-2xl mt-6 flex flex-col items-center text-center">
                    <ul
                      className="list-disc list-inside space-y-1 text-sm"
                      style={{ color: "#EF0BB8" }}
                    >
                      {historyItems.map(({ item, label }, index) => (
                        <li
                          key={item.id ?? index}
                          style={
                            (item.failedTipsCount ?? 0) > 0
                              ? { color: "#C62828" }
                              : undefined
                          }
                        >
                          {label}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </Box>
      </Modal>

      <Modal
        open={isConfirmationModalOpen}
        onClose={handleConfirmationCancel}
        aria-labelledby="confirmation-modal"
        aria-describedby="confirmation-modal-description"
      >
        <Box sx={confirmationModalStyle}>
          <div className="bg-gray-light text-center text-xl font-normal text-black py-6 px-2">
            <h1>{t("branches.manual-process.confirmation-title")}</h1>
          </div>
          <div className="p-6">
            <div className="text-center mb-4">
              <p className="text-sm text-gray-600">
                {pendingValue
                  ? t("branches.manual-process.confirmation-enable")
                  : t("branches.manual-process.confirmation-disable")}
              </p>
            </div>

            <div className="flex gap-3 justify-center mt-8">
              <Button
                onClick={handleConfirmationCancel}
                disabled={isLoading}
                sx={{
                  color: "#898989",
                  fontFamily: "Montserrat",
                  textTransform: "none",
                  fontWeight: "400",
                }}
              >
                {t("cancel")}
              </Button>
              <Button
                onClick={handleToggle}
                disabled={isLoading}
                sx={{
                  backgroundColor: "#EF0BB8",
                  color: "#fff",
                  fontFamily: "Montserrat",
                  textTransform: "none",
                  fontWeight: "400",
                  "&:hover": {
                    backgroundColor: "#d009a0",
                  },
                  "&:disabled": {
                    backgroundColor: "#ccc",
                    color: "#666",
                  },
                }}
              >
                {isLoading ? t("loading") : t("branches.manual-process.confirm")}
              </Button>
            </div>
          </div>
        </Box>
      </Modal>

      <LoadingModal isOpen={isPageLoading} />
    </>
  );
}
